import { z } from "zod";
import dayjs from "dayjs";

// TODO(LH-2806): [FE][API] Integrate Table Data and Sorting API — grouped `listResponse`,
// `followUpStatus` → row merge, and `flattenUcccOfdShipmentsList` match current dummy/MSW only;
// validate row fields, sort metadata, and pagination envelope with the real API and keep
// `useUcccOfdShipments` + MSW in sync.

export const ucccOfdShipmentsSchema = (() => {
  const dayjsRequired = z
    .union([z.string(), z.date()])
    .transform((val) => dayjs(val))
    .refine((d) => d.isValid(), { message: "Invalid date" });

  const dayjsOptionalNullable = z
    .union([z.string(), z.date(), z.null()])
    .optional()
    .transform((val) => {
      if (val === null || val === undefined) return null;
      const d = dayjs(val);
      return d.isValid() ? d : null;
    });

  /** API may send null for lhRegionId on SIC objects; normalize for the client. */
  const sic = z.object({
    id: z.number(),
    code: z.string(),
    ownership: z.string(),
    lhRegionId: z.preprocess(
      (val) => (val === null || val === undefined ? 0 : val),
      z.number()
    ),
    timeZone: z.string(),
  });

  const consignee = z.object({
    id: z.number(),
    name: z.string(),
    address: z.string(),
    city: z.string(),
    state: z.string(),
    zipCode: z.string(),
  });

  const row = z
    .object({
      shipmentId: z.number(),
      proNumber: z.string(),
      pickupDateUtc: dayjsRequired,
      dueDate: dayjsRequired,
      deliveryDateUtc: dayjsOptionalNullable,
      appointmentStartTimestampUtc: dayjsOptionalNullable,
      originSic: sic,
      destinationSic: sic,
      currentSic: sic.nullable(),
      finalDestination: z.string(),
      pts: z.number(),
      pcs: z.number(),
      weight: z.number(),
      shipmentTags: z.array(z.string()),
      status: z.string(),
      /** FB follow-up label from API (e.g. EXPEDITE, SERV FAILURE); merged into `fbFollowUpStatus` when not overridden. */
      followUpStatus: z.string().nullable().optional(),
      tripNumber: z.number().nullable(),
      etaUtc: dayjsOptionalNullable,
      ataUtc: dayjsOptionalNullable,
      etaStatus: z
        .string()
        .nullable()
        .optional()
        .transform((v) => v ?? null),
      locationDetails: z.unknown().nullable().optional(),
      consignee,
      /** Optional client id; defaults to `String(shipmentId)` after parse. */
      id: z.string().optional(),
      noDimensions: z.boolean().optional(),
      serviceFailure: z.boolean().optional(),
      expediteShipment: z.boolean().optional(),
      hasProWarning: z.boolean().optional(),
      hasProExpedite: z.boolean().optional(),
      deliveryDate: z.string().optional(),
      arrivalToSic: z.string().optional(),
      arrivalToSicStatus: z.enum(["LATE", "ON TIME"]).optional(),
      appt: z.string().optional(),
      fbFollowUpStatus: z.string().optional(),
      currentZone: sic.optional(),
    })
    .transform((r) => {
      const followUpFromApi = r.followUpStatus ?? null;
      const trimmedFollowUp =
        followUpFromApi && followUpFromApi.trim() !== ""
          ? followUpFromApi.trim()
          : null;
      const fbFollowUpStatus =
        r.fbFollowUpStatus ?? trimmedFollowUp ?? undefined;
      const expediteFromFollowUp =
        trimmedFollowUp !== null &&
        trimmedFollowUp.toUpperCase() === "EXPEDITE";
      return {
        ...r,
        id: r.id ?? String(r.shipmentId),
        locationDetails: r.locationDetails ?? null,
        followUpStatus: followUpFromApi,
        fbFollowUpStatus,
        hasProExpedite: r.hasProExpedite ?? expediteFromFollowUp,
      };
    });

  const ownerGroup = z.object({
    ownerName: z.string(),
    ownerId: z.string(),
    shipmentCount: z.number(),
    shipments: z.array(row),
  });

  const listResponse = z.object({
    unassignedShipmentCount: z.number(),
    unassignedShipments: z.array(row),
    ownerGroups: z.array(ownerGroup),
  });

  return { row, listResponse, ownerGroup };
})();

export type UcccOfdShipmentRow = z.infer<typeof ucccOfdShipmentsSchema.row>;

export type UcccOfdShipmentsListResponse = z.infer<
  typeof ucccOfdShipmentsSchema.listResponse
>;

/** All shipment rows across unassigned pool and owner-assigned groups (unique `id` / `shipmentId`). */
export const flattenUcccOfdShipmentsList = (
  list: UcccOfdShipmentsListResponse
): UcccOfdShipmentRow[] => [
  ...list.unassignedShipments,
  ...list.ownerGroups.flatMap((g) => g.shipments),
];

// --- UC/CC OFD dashboard-summary API ---

export const ucccOfdDashboardSummarySchema = z.object({
  totalUcCcShipments: z.number(),
  serviceFailureCount: z.number(),
  deliveredCount: z.number(),
  onTimePerformance: z.object({
    percentage: z.number(),
    onTimeCount: z.number(),
    total: z.number(),
  }),
  followUpStatuses: z.object({
    delivered: z.number(),
    expedite: z.number(),
    ofd: z.number(),
    osd: z.number(),
    pendingAppt: z.number(),
    pendingPod: z.number(),
    serviceFailure: z.number(),
    noStatus: z.number(),
  }),
});

export type UcccOfdDashboardSummaryData = z.infer<
  typeof ucccOfdDashboardSummarySchema
>;

export type UcccOfdFbFollowUpStatusCounts =
  UcccOfdDashboardSummaryData["followUpStatuses"];

export const EMPTY_UCCC_OFD_DASHBOARD_SUMMARY: UcccOfdDashboardSummaryData = {
  totalUcCcShipments: 0,
  serviceFailureCount: 0,
  deliveredCount: 0,
  onTimePerformance: {
    percentage: 0,
    onTimeCount: 0,
    total: 0,
  },
  followUpStatuses: {
    delivered: 0,
    expedite: 0,
    ofd: 0,
    osd: 0,
    pendingAppt: 0,
    pendingPod: 0,
    serviceFailure: 0,
    noStatus: 0,
  },
};
